import { useEffect, useMemo, useState } from 'react'
import { useCurrentClubStore } from '@/core/auth/current-club-store'
import {
  isSubscriptionTrialActive,
  isSubscriptionTrialExpired,
  subscriptionTrialDaysRemaining,
  trialReminderThreshold,
  type Subscription,
} from '@/core/models/subscription'
import type { ClubStatus, BillingStatus } from '@/core/models/club-status'
import { useClub } from './use-club'

export interface CurrentSubscriptionResult {
  subscription: Subscription | null
  isLoading: boolean
  isError: boolean
  /** Lifecycle status of the club: mockup / trial / active / closed. */
  status: ClubStatus | null
  /** Billing status: not_billable / current / past_due / exempt. */
  billingStatus: BillingStatus | null
  /** Trial ongoing. */
  isTrial: boolean
  /** Trial is over but no paid plan. The main "blocked" state. */
  isExpired: boolean
  /** Days remaining in the trial. 0 when expired or not on trial. */
  daysRemaining: number
  /** 30 / 14 / 3 / null — drives badge color. */
  reminderThreshold: number | null
  /**
   * True when the club can still mutate data. False when billing is
   * past_due (read-only) or the club is closed. Trial / active stay
   * fully mutable.
   */
  canMutate: boolean
  /** Whether the user's club is on the permanent founder rate. */
  isPartnerClub: boolean
}

/**
 * Resolves the current club's lifecycle + billing + subscription state from
 * the `clubs/{clubId}` document. Reads the full club (lifecycle `status` and
 * `billingStatus` live top-level now; the nested `subscription` map holds only
 * Stripe metadata) so consumers can `const sub = useCurrentSubscription()`
 * without wiring clubId themselves.
 */
export function useCurrentSubscription(nowOverride?: Date): CurrentSubscriptionResult {
  const clubId = useCurrentClubStore((s) => s.clubId)
  const query = useClub(clubId)
  const club = query.data ?? null
  const sub = club?.subscription ?? null

  // Tick once a minute so `daysRemaining` rolls over at midnight without a
  // page reload — a user staring at "3 días" before bed should see "2 días"
  // by morning. Tests pass a fixed `nowOverride` to disable the ticker.
  const [tickedNow, setTickedNow] = useState<Date>(() => nowOverride ?? new Date())
  useEffect(() => {
    if (nowOverride) return
    const id = window.setInterval(() => setTickedNow(new Date()), 60_000)
    return () => window.clearInterval(id)
  }, [nowOverride])
  const now = nowOverride ?? tickedNow
  // Day key keeps the memo stable through the 60s ticks UNTIL the calendar
  // day changes — we don't want to recompute trial flags on every minute,
  // only when the rollover actually crosses midnight.
  const dayKey = useMemo(
    () => `${now.getFullYear()}-${now.getMonth()}-${now.getDate()}`,
    [now],
  )

  return useMemo<CurrentSubscriptionResult>(() => {
    if (club == null || sub == null) {
      return {
        subscription: null,
        isLoading: query.isLoading,
        isError: query.isError,
        status: club?.status ?? null,
        billingStatus: club?.billingStatus ?? null,
        isTrial: false,
        isExpired: false,
        daysRemaining: 0,
        reminderThreshold: null,
        canMutate: false,
        isPartnerClub: false,
      }
    }
    const status = club.status
    const billingStatus = club.billingStatus
    const isTrial = isSubscriptionTrialActive(sub, status, now)
    // A by-the-clock-expired trial with a card on file is NOT blocked — the
    // club has committed to pay and Stripe owns the trial→active handoff (see
    // subscriptionCanMutate). Drives write-affordance enablement, so it must
    // agree with the mutation gate or buttons would disable on a paying club.
    const trialExpiredUnpaid =
      isSubscriptionTrialExpired(sub, status, now) && !sub.paymentMethodAdded
    // Exempt clubs are never "expired" — exemption supersedes every billing
    // lapse, mirroring `subscriptionCanMutate` + `clubAccess`.
    const isExpired =
      billingStatus !== 'exempt' &&
      (trialExpiredUnpaid || status === 'closed' || billingStatus === 'past_due')
    return {
      subscription: sub,
      isLoading: query.isLoading,
      isError: query.isError,
      status,
      billingStatus,
      isTrial,
      isExpired,
      daysRemaining: subscriptionTrialDaysRemaining(sub, now),
      reminderThreshold: trialReminderThreshold(sub, status, now),
      canMutate: !isExpired,
      isPartnerClub: sub.partnerClub,
    }
    // `now` itself is excluded — we recompute when the day key flips, not on
    // every minute tick. Query data and day rollover are the meaningful
    // inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [club, sub, query.isLoading, query.isError, dayKey])
}
